import { type PropsWithChildren } from "react";
import { ReloadIcon } from "vega-icons";

import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { ThemedButton } from "../Buttons/ThemedButton";
import { LoadingSpinner } from "../Loaders/LoadingSpinner/LoadingSpinner";
import { UnknownErrorModal } from "../Modals/UnknownErrorModal";

import { Footer } from "./Footer/Footer";
import { Container } from "./Container";

type Props = {
  error?: string | null;
  fullWidth?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  hideSwitchButton?: boolean;
  isLoading?: boolean;
  investPortalURL?: string;
  isLogin?: boolean;
  onErrorClose?: VoidFunction;
  className?: string;
};

export function OuterPageContainer({
  children,
  error,
  fullWidth,
  hideFooter,
  hideHeader,
  hideSwitchButton,
  isLoading,
  investPortalURL,
  isLogin = false,
  onErrorClose,
}: PropsWithChildren<Props>) {
  const {
    theme: { logos },
  } = usePublicConfig();
  const { theme } = useTheme();

  /**
   * TODO
   */
  const switchToInvestorPortal = () => {
    if (investPortalURL) {
      window.location.href = investPortalURL;
    }
  };

  return (
    <>
      <UnknownErrorModal onClose={onErrorClose} open={isTruthy(error)} />
      {isTruthy(isLoading) ? <LoadingSpinner /> : null}
      <Container className="bg-background/secondary">
        <div className="flex flex-col flex-1">
          <div
            className={cnTwMerge(
              "flex h-full w-full flex-1 flex-col justify-between self-center",
              { "max-w-xl": isFalsy(fullWidth) },
            )}
          >
            <div
              className={cnTwMerge({
                "align-center flex h-full flex-1 flex-col justify-center":
                  isLogin,
              })}
            >
              {!isLogin && isFalsy(hideHeader) ? (
                <div className="flex items-center justify-between ml-2 mt-6">
                  <img
                    alt="Vega"
                    className="relative -left-2 h-12 w-auto max-w-xs lg:block"
                    src={theme === "dark" ? logos.logoDark : logos.logoLight}
                  />
                </div>
              ) : null}
              <div className="mt-10">{children}</div>
              {isTruthy(hideFooter) ? (
                <div className="flex w-full justify-center">
                  <Footer isLoggedIn={false} />
                </div>
              ) : null}
            </div>
            {isFalsy(hideSwitchButton) ? (
              <div className="flex flex-col items-center justify-center max-w-sm mx-auto pb-10">
                <ThemedButton
                  className="mt-6"
                  dataTestId="switch-button"
                  fullWidth
                  icon={<ReloadIcon />}
                  iconPosition="right"
                  onClick={switchToInvestorPortal}
                  variant="secondary"
                >
                  {m.btn_switchToInvestorPortal()}
                </ThemedButton>
              </div>
            ) : null}
          </div>
        </div>
      </Container>
    </>
  );
}
